import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Plus, Package, Eye, CheckCircle2, Search, ImagePlus, X } from 'lucide-react';

const emptyForm = {
  buyer_id: '', factory_id: '',
  style_name: '', quantity: '', delivery_date: '',
  pa_no: '', department: '', inspection_date: '',
  printing: 'no', embroidery: 'no', garment_wash: 'no', va_others: '',
  remarks: '', file_sent: 'no', file_sent_remarks: ''
};

const statusConfig = {
  'On Track': { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  'At Risk': { bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
  'Delayed': { bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-500' },
  'Completed': { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
};

const merchantTabs = [
  { key: 'active', label: 'Active Orders', icon: Package },
  { key: 'completed', label: 'Completed', icon: CheckCircle2 },
];

export default function MerchantDashboard() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [buyers, setBuyers] = useState([]);
  const [factories, setFactories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddOrder, setShowAddOrder] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });
  const [activeTab, setActiveTab] = useState('active');
  const [styleImage, setStyleImage] = useState(null);

  const fetchOrders = useCallback(async (lifecycle) => {
    setLoading(true);
    try {
      const res = await api.get(`/orders?lifecycle=${lifecycle}`);
      setOrders(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const [buyersRes, factoriesRes] = await Promise.all([
        api.get('/users/buyers'),
        api.get('/factories').catch(() => ({ data: [] }))
      ]);
      setBuyers(buyersRes.data);
      setFactories(factoriesRes.data);
    } catch (e) { toast.error('Failed to load'); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { fetchOrders(activeTab); }, [fetchOrders, activeTab]);

  const handleSaveOrder = async () => {
    if (!form.buyer_id || !form.style_name || !form.quantity) { toast.error('Fill required fields'); return; }
    setSaving(true);
    try {
      const payload = {
        buyer_id: form.buyer_id,
        style_name: form.style_name,
        quantity: parseInt(form.quantity, 10),
        delivery_date: form.delivery_date || null,
        pa_no: form.pa_no,
        department: form.department,
        inspection_date: form.inspection_date || null,
        remarks: form.remarks,
        file_sent: form.file_sent === 'yes',
        file_sent_remarks: form.file_sent_remarks,
        factory_id: form.factory_id || null,
        value_additions: {
          printing: form.printing === 'yes',
          embroidery: form.embroidery === 'yes',
          garment_wash: form.garment_wash === 'yes',
          others: form.va_others || '',
        },
      };
      const res = await api.post('/orders', payload);
      // Upload style image if selected
      if (styleImage && res.data?.id) {
        const imgData = new FormData();
        imgData.append('file', styleImage);
        await api.post(`/orders/${res.data.id}/image`, imgData, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      toast.success('Order created');
      setShowAddOrder(false);
      setForm({ ...emptyForm });
      setStyleImage(null);
      fetchOrders(activeTab);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setSaving(false);
  };

  if (loading) return <DashboardLayout><div className="flex justify-center py-20"><div className="w-6 h-6 border-2 border-[#1C1C1C] border-t-transparent rounded-full animate-spin" /></div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div data-testid="merchant-dashboard" className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl font-semibold text-[#0A0A0A]">My Orders</h1>
            <p className="text-sm text-[#71717A] mt-1">{orders.length} orders assigned</p>
          </div>
          <Dialog open={showAddOrder} onOpenChange={setShowAddOrder}>
            <DialogTrigger asChild>
              <Button className="bg-[#1C1C1C] hover:bg-[#0A0A0A] text-white rounded-md text-sm btn-premium" data-testid="merchant-add-order-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Order
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-heading text-lg">Create Order</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-2">
                {/* Style Image Upload */}
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wide text-[#52525B]">Style Image</Label>
                  {styleImage ? (
                    <div className="relative w-full h-32 rounded-md border border-black/[0.08] overflow-hidden bg-[#FAFAF8]">
                      <img src={URL.createObjectURL(styleImage)} alt="Style preview" className="w-full h-full object-contain" />
                      <button type="button" onClick={() => setStyleImage(null)} className="absolute top-2 right-2 p-1 rounded-full bg-black/60 text-white hover:bg-black/80 transition-colors">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center w-full h-24 rounded-md border-2 border-dashed border-black/[0.1] bg-[#FAFAF8] cursor-pointer hover:border-black/[0.2] transition-colors">
                      <ImagePlus className="w-5 h-5 text-[#A1A1AA] mb-1" />
                      <span className="text-xs text-[#A1A1AA]">Click to upload style image</span>
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => { if (e.target.files[0]) setStyleImage(e.target.files[0]); }} />
                    </label>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wide text-[#52525B]">Buyer *</Label>
                    <Select value={form.buyer_id} onValueChange={(v) => setForm(p => ({ ...p, buyer_id: v }))}>
                      <SelectTrigger className="h-10 rounded-md" data-testid="merchant-order-buyer-select"><SelectValue placeholder="Select buyer" /></SelectTrigger>
                      <SelectContent>{buyers.map(b => <SelectItem key={b.id} value={b.id}>{b.name} — {b.company || 'N/A'}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wide text-[#52525B]">Factory *</Label>
                    <Select value={form.factory_id} onValueChange={(v) => setForm(p => ({ ...p, factory_id: v }))}>
                      <SelectTrigger className="h-10 rounded-md" data-testid="merchant-order-factory-select"><SelectValue placeholder="Select factory" /></SelectTrigger>
                      <SelectContent>{factories.map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wide text-[#52525B]">Style Name *</Label>
                    <Input value={form.style_name} onChange={(e) => setForm(p => ({ ...p, style_name: e.target.value }))} className="h-10 rounded-md" data-testid="merchant-order-style-input" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wide text-[#52525B]">Quantity *</Label>
                    <Input type="number" value={form.quantity} onChange={(e) => setForm(p => ({ ...p, quantity: e.target.value }))} className="h-10 rounded-md" data-testid="merchant-order-qty-input" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wide text-[#52525B]">PA No</Label>
                    <Input value={form.pa_no} onChange={(e) => setForm(p => ({ ...p, pa_no: e.target.value }))} className="h-10 rounded-md" data-testid="merchant-order-pa-input" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wide text-[#52525B]">Department</Label>
                    <Input value={form.department} onChange={(e) => setForm(p => ({ ...p, department: e.target.value }))} className="h-10 rounded-md" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wide text-[#52525B]">Delivery Date</Label>
                    <Input type="date" value={form.delivery_date} onChange={(e) => setForm(p => ({ ...p, delivery_date: e.target.value }))} className="h-10 rounded-md" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wide text-[#52525B]">Inspection Date</Label>
                    <Input type="date" value={form.inspection_date} onChange={(e) => setForm(p => ({ ...p, inspection_date: e.target.value }))} className="h-10 rounded-md" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wide text-[#52525B]">Value Additions</Label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[['printing', 'Printing'], ['embroidery', 'Embroidery'], ['garment_wash', 'Garment Wash']].map(([key, label]) => (
                      <Select key={key} value={form[key]} onValueChange={(v) => setForm(p => ({ ...p, [key]: v }))}>
                        <SelectTrigger className="h-9 rounded-md text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="no">{label}: No</SelectItem><SelectItem value="yes">{label}: Yes</SelectItem></SelectContent>
                      </Select>
                    ))}
                    <Input value={form.va_others} onChange={(e) => setForm(p => ({ ...p, va_others: e.target.value }))} placeholder="Others..." className="h-9 rounded-md text-xs" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wide text-[#52525B]">File Sent</Label>
                    <Select value={form.file_sent} onValueChange={(v) => setForm(p => ({ ...p, file_sent: v }))}>
                      <SelectTrigger className="h-10 rounded-md" data-testid="merchant-file-sent-select"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="no">No</SelectItem><SelectItem value="yes">Yes</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wide text-[#52525B]">File Sent Remarks</Label>
                    <Input value={form.file_sent_remarks} onChange={(e) => setForm(p => ({ ...p, file_sent_remarks: e.target.value }))} placeholder="Notes about file..." className="h-10 rounded-md" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wide text-[#52525B]">Remarks</Label>
                  <Input value={form.remarks} onChange={(e) => setForm(p => ({ ...p, remarks: e.target.value }))} placeholder="Any notes..." className="h-10 rounded-md" />
                </div>
                <Button onClick={handleSaveOrder} disabled={saving} className="w-full bg-[#1C1C1C] hover:bg-[#0A0A0A] text-white rounded-md h-10 btn-premium" data-testid="merchant-save-order-btn">
                  {saving ? 'Saving...' : 'Create Order'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 bg-white rounded-md border border-black/[0.06] p-1 w-fit">
          {merchantTabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === tab.key ? 'bg-[#1C1C1C] text-white' : 'text-[#52525B] hover:bg-[#F5F5F5]'}`}
              data-testid={`merchant-tab-${tab.key}`}
            >
              <tab.icon className="w-3.5 h-3.5" />
              {tab.label}
            </button>
          ))}
        </div>

        <div className="bg-white rounded-md border border-black/[0.06] overflow-hidden" data-testid="merchant-orders-table">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#FAFAF8] border-b border-black/[0.06]">
                <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-[#A1A1AA]">Order ID</TableHead>
                <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-[#A1A1AA]">Style</TableHead>
                <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-[#A1A1AA]">Buyer</TableHead>
                <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-[#A1A1AA]">Qty</TableHead>
                <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-[#A1A1AA]">Status</TableHead>
                <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-[#A1A1AA]">Progress</TableHead>
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center text-sm text-[#A1A1AA] py-8">No orders yet</TableCell></TableRow>
              ) : orders.map(order => {
                const sc = statusConfig[order.status] || statusConfig['On Track'];
                return (
                  <TableRow key={order.id} className="table-row-hover cursor-pointer" onClick={() => navigate(`/dashboard/merchant/orders/${order.id}`)} data-testid={`merchant-order-row-${order.order_id}`}>
                    <TableCell className="text-sm font-medium text-[#0A0A0A]">{order.order_id}</TableCell>
                    <TableCell className="text-sm text-[#52525B]">{order.style_name}</TableCell>
                    <TableCell className="text-sm text-[#52525B]">{order.buyer_name}</TableCell>
                    <TableCell className="text-sm font-semibold text-[#0A0A0A]">{order.quantity?.toLocaleString()}</TableCell>
                    <TableCell><span className={`status-badge ${sc.bg} ${sc.text}`}><div className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />{order.status}</span></TableCell>
                    <TableCell className="text-sm font-semibold text-[#0A0A0A]">{order.progress}%</TableCell>
                    <TableCell><Eye className="w-4 h-4 text-[#A1A1AA]" /></TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>
    </DashboardLayout>
  );
}
